import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import classnames from 'classnames';
import axios from 'axios';
import { SYMFONY_API_BASE_URL } from '@config/apiConfig';
import { captureException } from '@sentry/react';
import ErrorWithCause from '../../../helpers/ErrorWithCause';
import Button from '@components/Buttons/Button';
import ModalStatusBanner from '@components/Modals/components/ModalStatusBanner';
import TextInput, { StyledErrorSpan } from '../../../components/Fields/TextInput';
import useStyles from '../useStyles';
import validateEmail from '../../../helpers/Validation/validateEmail';
const Contact = () => {
    const { classes } = useStyles();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [working, setWorking] = useState(false);
    const [status, setStatus] = useState('idle');
    const [errors, setErrors] = useState({});
    const handleNameChange = (e) => {
        setName(e.currentTarget.value);
        setErrors({});
    };
    const handleEmailChange = (e) => {
        setEmail(e.currentTarget.value);
        setErrors({});
    };
    const handleMessageChange = (e) => {
        setMessage(e.currentTarget.value);
        setErrors({});
    };
    const matchServerErrors = (errorArray) => {
        return errorArray.reduce((previous, current) => {
            switch (current) {
                case 'First Name is required':
                case 'Invalid sender name':
                    previous.name = current;
                    break;
                case 'Email Address is required':
                case 'The email format is invalid':
                case 'Invalid sender email address':
                    previous.email = current;
                    break;
                case 'Message is required':
                case 'Invalid message content':
                    previous.message = current;
                    break;
                default:
                    previous.other = current;
            }
            return previous;
        }, {});
    };
    const handleSubmit = async (e) => {
        var _a, _b, _c;
        e.preventDefault();
        const validationErrors = {};
        if (!name) {
            validationErrors.name = 'First Name is required';
        }
        if (!email) {
            validationErrors.email = 'Email Address is required';
        }
        else if (validateEmail({ email }).fails()) {
            validationErrors.email = 'The email format is invalid';
        }
        if (!message) {
            validationErrors.message = 'Message is required';
        }
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setWorking(true);
        const payload = {
            fullName: name,
            emailAddress: email.trim(),
            message,
        };
        const requestName = 'brand_enquiry';
        try {
            await axios.post(`${SYMFONY_API_BASE_URL}/api/brand-enquiry/v1`, payload);
            setWorking(false);
            setStatus('success');
        }
        catch (error) {
            const err = new ErrorWithCause(error);
            setWorking(false);
            const errorArr = (_c = (_b = (_a = err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.data) !== null && _c !== void 0 ? _c : [];
            setErrors(matchServerErrors(errorArr));
            setStatus('error');
            captureException(err, {
                extra: {
                    request: requestName,
                },
            });
        }
    };
    return (_jsx("section", { id: "contact", className: classes.contactWrapper, "data-testid": "contact-section", children: _jsxs("div", { className: classnames(classes.container, classes.contactContainer), children: [_jsxs("div", { className: classes.contactContent, children: [_jsx("h2", { className: classes.contactHeading, "data-testid": "contact-section-heading", children: "We're ready to take your esports campaigns to the Next Level!" }), _jsx("p", { children: "Tell us your goals and we'll help you bring them to life. We're excited to expand your relationship with competitive gamers!" }), _jsxs("p", { children: ["Fill out our contact form or email ", _jsx("a", { href: "mailto:brands@repeat.gg", children: "brands@repeat.gg" })] })] }), _jsxs("div", { children: [status === 'error' && (_jsxs("div", { className: classes.contactForm, children: [_jsx("h3", { className: classes.contactErrorHeading, "data-testid": "contact-form-error", children: "Something went wrong" }), _jsx("p", { children: "Please confirm your details and try again" })] })), status !== 'success' && (_jsxs("form", { onSubmit: handleSubmit, className: classes.contactForm, children: [_jsx(TextInput, { id: "name", "data-testid": "contact-form-name-input", className: classes.contactFormTextInput, label: "Full Name", type: "text", placeholder: "Enter Full Name...", onChange: handleNameChange, error: !!errors.name, errorMessage: errors.name }), _jsx(TextInput, { id: "email", "data-testid": "contact-form-email-input", className: classes.contactFormTextInput, label: "Email Address", type: "email", placeholder: "Enter Email Address...", onChange: handleEmailChange, error: !!errors.email, errorMessage: errors.email }), _jsxs("label", { className: classnames(classes.contactFormLabel, classes.contactFormTextAreaLabel), htmlFor: "message", children: ["Message", _jsx("textarea", { placeholder: "Enter Message...", className: classes.contactFormTextArea, id: "message", "data-testid": "contact-form-message-text-area", name: "message", onChange: handleMessageChange }), !!errors.message && (_jsx(StyledErrorSpan, { role: "alert", textAlign: "right", className: classnames({ showError: !!errors.message }), children: errors.message }))] }), _jsx(Button, { loading: working, type: "submit", size: "lg", block: true, children: "Send us a Message" })] })), status === 'success' && (_jsxs("div", { className: classes.contactForm, children: [_jsx(ModalStatusBanner, { status: status }), _jsx("h3", { className: classes.contactSuccessHeading, "data-testid": "contact-modal-success-h3", children: "We've received your enquiry" }), _jsx("p", { children: "We've received your enquiry and will get back to you soon." })] }))] })] }) }));
};
export default Contact;
